import { ReactNode } from 'react';
import { message } from 'antd';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import type { CredentialResponse } from '@react-oauth/google';
import { setSynqSentryUser } from '@admin-panel/observability';
import AuthLayout from '../../../auth/AuthLayout';
import { useVerifyGoogleAuthMutation } from '../../store/identity/identityApi';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { setErpUser, setTokens } from '../../store/identity/slice';

const GOOGLE_CLIENT_ID = '677748884621-2rb9sde5lq7na2dmn8nlpa7a5jlsnr4p.apps.googleusercontent.com';

type LoginError = {
  data?: {
    message?: string;
  };
};

export const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [verifyGoogleAuth] = useVerifyGoogleAuthMutation();

  const handleSuccess = async (response: CredentialResponse) => {
    if (response.credential) {
      try {
        const result = await verifyGoogleAuth({
          id: response.credential,
          authMode: 'GOOGLE',
        }).unwrap();

        dispatch(
          setTokens({
            token: result.token,
            refreshToken: result.refreshToken,
          }),
        );
        dispatch(setErpUser(result.erpUser));
        localStorage.setItem('user', JSON.stringify(result.erpUser));
        localStorage.setItem('at', result.token);
        localStorage.setItem('rt', result.refreshToken);
        setSynqSentryUser(result.erpUser);

        message.success('Login successful!');
        navigate('/');
      } catch (error) {
        const loginError = error as LoginError;
        console.error('Login error:', error);
        message.error(loginError?.data?.message || 'Login failed');
      }
    }
  };

  const handleFailure = () => {
    message.error('Could not log you in, Try with another method');
  };

  return (
    <AuthLayout
      consoleName="SYNQ"
      title="Sign in to Synq"
      subtitle="Use your Google account to continue."
      brandColor="#171717"
    >
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <GoogleLogin
          auto_select
          useOneTap
          onSuccess={handleSuccess}
          onError={handleFailure}
          text="signin_with"
          shape="pill"
          size="large"
          width="100%"
        />
      </GoogleOAuthProvider>
    </AuthLayout>
  );
};

interface IRouteProtectorProps {
  children: ReactNode;
}

export const RouteProtector = ({ children }: IRouteProtectorProps) => {
  const token = useAppSelector(store => store.identity.token);
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};
